(() => {
  const roots = [...document.querySelectorAll('[data-littleg-tool-marketplace]')];
  if (!roots.length) return;

  const MAX_TEXT_LENGTH = 180;
  const instances = new Map();

  /*
   * Core adapter contract:
   * - Listen for bubbled `littleg:tool-marketplace-request` events.
   * - Resolve purchase status and creator attribution against the authenticated
   *   server session; do not accept a customer ID from the storefront.
   * - Dispatch `littleg:tool-marketplace-data` on document with detail:
   *   { panelId, requestId, products: [{ id, purchaseStatus, creatorName,
   *     previewAvailable, previewUrl }] }.
   *   purchaseStatus is `purchased`, `not_purchased`, or `sign_in_required`.
   *   Only return creator details that are approved for public display.
   * - Shopify Liquid owns catalog identity, product URLs and rendered prices.
   */

  const safeText = (value) => typeof value === 'string' ? value.trim().slice(0, MAX_TEXT_LENGTH) : '';

  const safeInternalUrl = (value) => {
    if (typeof value !== 'string' || value.length > 2048 || value.startsWith('//')) return null;
    try {
      const url = new URL(value, window.location.origin);
      if (url.protocol !== 'https:' || url.origin !== window.location.origin || url.username || url.password) return null;
      return url.href;
    } catch {
      return null;
    }
  };

  const makeRequestId = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  };

  const setPurchaseState = (root, card, purchaseStatus) => {
    const state = card.querySelector('[data-market-purchase-status]');
    const messages = {
      purchased: root.dataset.purchasedLabel,
      not_purchased: root.dataset.notPurchasedLabel,
      sign_in_required: root.dataset.signInLabel
    };
    const validStates = new Set(['purchased', 'not_purchased', 'sign_in_required']);
    const resolvedState = validStates.has(purchaseStatus) ? purchaseStatus : 'unknown';
    state.dataset.status = resolvedState;
    state.textContent = messages[resolvedState] || root.dataset.statusUnavailableLabel || 'Status unavailable';
  };

  const filterCards = (root, selectedCategory) => {
    const cards = [...root.querySelectorAll('[data-market-tool-card]')];
    let visibleCount = 0;
    cards.forEach((card) => {
      const matches = selectedCategory === '*' || card.dataset.category === selectedCategory;
      card.hidden = !matches;
      if (matches) visibleCount += 1;
    });
    const empty = root.querySelector('[data-market-empty-filter]');
    if (empty) empty.hidden = visibleCount > 0;
  };

  const renderFilters = (root) => {
    const container = root.querySelector('[data-market-filters]');
    const cards = [...root.querySelectorAll('[data-market-tool-card]')];
    if (!container || !cards.length) return;

    const categories = [...new Set(cards.map((card) => card.dataset.category).filter(Boolean))];
    const allButton = document.createElement('button');
    allButton.type = 'button';
    allButton.className = 'littleg-tool-marketplace__filter';
    allButton.dataset.marketplaceFilter = '*';
    allButton.setAttribute('aria-pressed', 'true');
    allButton.textContent = root.dataset.allLabel || 'All tools';
    const buttons = [allButton, ...categories.map((category) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'littleg-tool-marketplace__filter';
      button.dataset.marketplaceFilter = category;
      button.setAttribute('aria-pressed', 'false');
      button.textContent = category;
      return button;
    })];
    container.replaceChildren(...buttons);
    container.addEventListener('click', (event) => {
      const button = event.target.closest('[data-marketplace-filter]');
      if (!button || !container.contains(button)) return;
      container.querySelectorAll('[data-marketplace-filter]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === button));
      });
      filterCards(root, button.dataset.marketplaceFilter);
    });
  };

  const handleMarketplaceData = (event) => {
    const payload = event.detail;
    if (!payload || typeof payload !== 'object') return;
    const instance = instances.get(payload.panelId);
    if (!instance || payload.requestId !== instance.requestId) return;

    const { root } = instance;
    if (instance.timer) window.clearTimeout(instance.timer);
    instance.timer = null;
    const status = root.querySelector('[data-market-status]');
    if (!Array.isArray(payload.products)) {
      root.dataset.state = 'unavailable';
      root.querySelectorAll('[data-market-tool-card]').forEach((card) => setPurchaseState(root, card, 'unknown'));
      status.textContent = root.dataset.unavailableMessage || 'Purchase status and previews are temporarily unavailable.';
      return;
    }

    const productData = new Map(payload.products.map((item) => [String(item?.id || ''), item]).filter(([id]) => id));
    root.querySelectorAll('[data-market-tool-card]').forEach((card) => {
      const item = productData.get(card.dataset.productId);
      setPurchaseState(root, card, item?.purchaseStatus);

      const creator = card.querySelector('[data-market-creator]');
      const creatorName = safeText(item?.creatorName);
      if (creatorName) {
        card.querySelector('[data-market-creator-name]').textContent = creatorName;
        creator.hidden = false;
      } else {
        creator.hidden = true;
      }

      const preview = card.querySelector('[data-market-preview]');
      const previewUrl = item?.previewAvailable === true ? safeInternalUrl(item.previewUrl) : null;
      if (previewUrl) {
        preview.href = previewUrl;
        preview.textContent = root.dataset.previewLabel || 'Free preview';
        preview.hidden = false;
      } else {
        preview.removeAttribute('href');
        preview.hidden = true;
      }
    });

    root.dataset.state = 'ready';
    status.textContent = root.dataset.loadedMessage || 'Tool details updated.';
  };

  document.addEventListener('littleg:tool-marketplace-data', handleMarketplaceData);

  roots.forEach((root) => {
    if (!root.id || instances.has(root.id)) return;
    const cards = [...root.querySelectorAll('[data-market-tool-card]')];
    const status = root.querySelector('[data-market-status]');
    if (!cards.length || !status) return;

    renderFilters(root);
    const timeoutMs = Math.min(Math.max(Number(root.dataset.loadingTimeout) || 10000, 3000), 30000);
    const instance = { root, requestId: '', timer: null, timeoutMs };
    instances.set(root.id, instance);

    const requestData = () => {
      if (instance.timer) window.clearTimeout(instance.timer);
      instance.requestId = makeRequestId();
      root.dataset.state = 'loading';
      status.textContent = root.dataset.loadingMessage || 'Checking your purchase status…';
      instance.timer = window.setTimeout(() => {
        if (root.dataset.state !== 'loading') return;
        root.dataset.state = 'unavailable';
        cards.forEach((card) => setPurchaseState(root, card, 'unknown'));
        status.textContent = root.dataset.unavailableMessage || 'Purchase status and previews are temporarily unavailable.';
      }, instance.timeoutMs);
      root.dispatchEvent(new CustomEvent('littleg:tool-marketplace-request', {
        bubbles: true,
        detail: {
          panelId: root.id,
          requestId: instance.requestId,
          products: cards.map((card) => ({ id: card.dataset.productId }))
        }
      }));
    };

    requestData();
  });
})();
